import React, { useState, useContext } from 'react';

import { Table } from 'antd';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadPayment, forceReloadPayments, useDeleteRow } from '@hooks/API/UsePayments';

import { StatementTableContainer } from '@components/ContactsStatement/StatementUI';
import VerticalDate from '@components/Dates/VerticalDate';
import EmptyState from '@components/Gui/EmptyState';
import { NwRemoveButton } from '@components/Gui/NwButton';
import ProjectTitle from '@components/Project/ProjectTitle';
import { getCommissionLabel, isMainStatementLine } from '@components/StatementRowsCommon/helpers';
import TransactionDetailDrawer from '@components/Transactions/TransactionsDrawer/TransactionDetailDrawer';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import PaymentStatementTotalDetails from './PaymentStatementTotalDetails';

const PaymentStatementLines = ({ paymentData }) => {
    const [selectedTransaction, setSelectedTransaction] = useState();
    const [showTransactionDetailDrawer, setShowTransactionDetailDrawer] = useState(false);
    const { mutateAsync: onDeleteRow } = useDeleteRow();

    const enumerators = [...useContext(ListContext).cachedList.enumerators.items];
    const customertypes = enumerators.find((el) => {
        return el.Name === 'CustomerTypeEnum';
    }).Values;
    const statementType =
        paymentData.Type === 'Model'
            ? 'model'
            : customertypes.find((el) => el.Value === paymentData.Contact.SubType)?.Name?.toLowerCase() ||
              paymentData.Type.toLowerCase();

    const handleCloseDetailDrawer = () => {
        setShowTransactionDetailDrawer(false);
        setSelectedTransaction(null);
    };

    const handleEditTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionDetailDrawer(true);
    };

    const onDelete = async (record) => {
        try {
            await onDeleteRow({
                data: {
                    PaymentID: paymentData.ID,
                    StatementID: record.ID,
                },
            });
        } catch (error) {
            return null;
        } finally {
            forceReloadPayment(paymentData.ID);
            forceReloadPayments(paymentData.ContactID);
        }
    };

    const displayedStatements = paymentData.Statements.map((st) => {
        return {
            ...st,
            key: st.ID,
        };
    });

    const columns = [
        {
            title: 'Date',
            dataIndex: 'Date',
            key: 'Date',
            onCell: (record) => ({
                onClick: () => {
                    handleEditTransaction(record.Transaction);
                },
            }),
            render: (text, record) => {
                if (isMainStatementLine(record)) {
                    return (
                        <div className="date-container">
                            <VerticalDate size="small" date={record.RefDate} verticalPadding="0" />
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: 'Title',
            key: 'Title',
            width: '30%',
            onCell: (record) => ({
                onClick: () => {
                    handleEditTransaction(record.Transaction);
                },
            }),
            render: (record) => {
                if (isMainStatementLine(record)) {
                    return (
                        <div className="transaction-details">
                            {paymentData.Type !== 'Model' && record.Transaction.ModelID && (
                                <p>{getContactName(record.Transaction.ModelID)}</p>
                            )}
                            <TransactionTitle
                                transaction={record.Transaction}
                                target={paymentData.Type.toLowerCase()}
                            />
                            <small>{record.Description || <em className="super-muted">no description</em>}</small>
                        </div>
                    );
                } else {
                    return (
                        <div className="transaction-details">
                            <small>{getCommissionLabel({ statementType, variant: 'long' }).toLowerCase()}</small>
                        </div>
                    );
                }
            },
        },
        {
            title: 'Project',
            dataIndex: 'Project',
            key: 'Project',
            width: '50%',
            className: 'top-aligned',
            onCell: (record) => ({
                onClick: () => {
                    handleEditTransaction(record.Transaction);
                },
            }),
            render: (text, record) => {
                if (isMainStatementLine(record)) {
                    return (
                        <div className="project-details">
                            {record.Transaction.Project && (
                                <ProjectTitle project={record.Transaction.Project} compact />
                            )}
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: 'Net Amount',
            key: 'Amount',
            width: '20%',
            align: 'right',
            onCell: (record) => ({
                onClick: () => {
                    handleEditTransaction(record.Transaction);
                },
            }),
            render: (record) => {
                return (
                    <div className="statement-total">
                        <PaymentStatementTotalDetails statement={record} />
                    </div>
                );
            },
        },
        {
            title: 'Remove',
            dataIndex: 'Actions',
            key: 'Actions',
            render: (text, record) => {
                return (
                    <NwRemoveButton
                        iconOnly
                        ghost
                        onClick={() => {
                            onDelete(record);
                        }}
                    />
                );
            },
        },
    ];

    return (
        <StatementTableContainer>
            {paymentData.Statements.length > 0 ? (
                <Table
                    showSizeChanger
                    pagination={false}
                    columns={columns}
                    dataSource={displayedStatements}
                    className="statement-table statement-table-payment"
                    rowClassName={(record, index) => {
                        if (record.isCommission) {
                            return 'commission-row';
                        }
                        if (record.isClientAdvance) {
                            return 'advance-row';
                        }
                        return '';
                    }}
                />
            ) : (
                <EmptyState message="No Results" />
            )}
            {showTransactionDetailDrawer && (
                <TransactionDetailDrawer
                    transaction={selectedTransaction}
                    projectId={selectedTransaction.Project ? selectedTransaction.Project.ID : null}
                    onClose={handleCloseDetailDrawer}
                    onUpdateTransaction={() => {
                        // keep drawer open; data reload is handled by child via forceReloadTransaction
                    }}
                    onTransactionRemoved={() => {
                        setShowTransactionDetailDrawer(false);
                        setSelectedTransaction(null);
                        //onUpdateTransaction();
                    }}
                />
            )}
        </StatementTableContainer>
    );
};

export default PaymentStatementLines;
